import axios, { AxiosError } from 'axios'
import type {
  User,
  LoginRequest,
  LoginResponse,
  Delivery,
  Availability,
  Earning,
  EarningsSummary,
  Vehicle,
  VehicleType,
} from '@/types'

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Redirect to login if not authenticated
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<LoginResponse>('/auth/login', data)
    return response.data
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout')
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<{
      userId: string
      email: string
      fullName: string | null
      phoneNumber: string | null
      isActive: boolean
      createdAt: string
      roles: string[]
    }>('/auth/me')
    // Map API response to User type
    return {
      id: response.data.userId,
      email: response.data.email,
      name: response.data.fullName || response.data.email.split('@')[0],
      phone: response.data.phoneNumber || undefined,
      role: response.data.roles[0] || 'courier',
      createdAt: response.data.createdAt,
    }
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await api.put('/auth/password', { currentPassword, newPassword })
  },
}

// Courier API
export const courierApi = {
  // Deliveries
  getDeliveries: async (status?: string): Promise<Delivery[]> => {
    const params = status ? { status } : {}
    const response = await api.get<Delivery[]>('/courier/deliveries', { params })
    return response.data
  },

  pickupOrder: async (orderId: string, notes?: string): Promise<void> => {
    await api.post(`/courier/deliveries/${orderId}/pickup`, { notes })
  },

  deliverOrder: async (orderId: string, proofOfDelivery?: string, notes?: string): Promise<void> => {
    await api.post(`/courier/deliveries/${orderId}/deliver`, { proofOfDelivery, notes })
  },

  // Availability
  getAvailability: async (): Promise<Availability> => {
    const response = await api.get<Availability>('/courier/availability')
    return response.data
  },

  updateAvailability: async (isOnline: boolean, zone?: string): Promise<void> => {
    await api.put('/courier/availability', { isOnline, zone })
  },

  // Earnings
  getEarnings: async (params?: {
    status?: string
    startDate?: string
    endDate?: string
  }): Promise<Earning[]> => {
    const response = await api.get<Earning[]>('/courier/earnings', { params })
    return response.data
  },

  getEarningsSummary: async (params?: {
    startDate?: string
    endDate?: string
  }): Promise<EarningsSummary> => {
    const response = await api.get<EarningsSummary>('/courier/earnings/summary', { params })
    return response.data
  },

  // Vehicle
  getVehicle: async (): Promise<Vehicle | null> => {
    try {
      const response = await api.get<Vehicle>('/courier/vehicle')
      return response.data
    } catch {
      return null
    }
  },

  registerVehicle: async (data: {
    type: string
    make?: string
    model?: string
    licensePlate?: string
    color?: string
  }): Promise<Vehicle> => {
    const response = await api.post<Vehicle>('/courier/vehicle', data)
    return response.data
  },

  updateVehicle: async (data: {
    type?: string
    make?: string
    model?: string
    licensePlate?: string
    color?: string
    isActive?: boolean
  }): Promise<Vehicle> => {
    const response = await api.put<Vehicle>('/courier/vehicle', data)
    return response.data
  },

  getVehicleTypes: async (): Promise<VehicleType[]> => {
    const response = await api.get<VehicleType[]>('/courier/vehicle/types')
    return response.data
  },
}

export default api
